/**
 * Texts of the building blocks (1.19). German is the master, English has the same keys. The app
 * sets the device language once at start (`setUiLocale`); components read `uiText()` while
 * rendering – never at module level, so the order of imports does not matter.
 */
export type UiLocale = 'de' | 'en';
let current: UiLocale = 'de';

export function setUiLocale(locale: UiLocale) {
  current = locale;
}
export const uiLocale = () => current;
export const uiNumber = (n: number) =>
  new Intl.NumberFormat(current === 'de' ? 'de-DE' : 'en-GB').format(n);

const starsDe = (n: number) => (n === 1 ? '1 Stern' : `${n} Sterne`);
const starsEn = (n: number) => (n === 1 ? '1 star' : `${n} stars`);

export const UI_TEXT = {
  de: {
    close: 'Schließen',
    password: 'Passwort',
    showPassword: 'Passwort anzeigen',
    again: 'Nochmal',
    mainNav: 'Hauptnavigation',
    more: 'Mehr',
    allAreas: 'Alle Bereiche',
    everyone: 'Alle',
    now: 'Jetzt',
    happeningNow: 'Läuft gerade',
    upNext: 'Als Nächstes',
    upNextLabel: (title: string, when: string) => `Als Nächstes: ${title}, ${when}`,
    upNextAt: (when: string) => `Als Nächstes · ${when}`,
    free: 'frei',
    duration: (min: number) =>
      min >= 60
        ? `${Math.floor(min / 60)} Std.${min % 60 ? ` ${min % 60} Min.` : ''}`
        : `${min} Min.`,
    freeFor: (duration: string) => `${duration} frei`,
    overlapWith: (title: string) => `gleichzeitig mit ${title}`,
    allDay: 'Ganztägig',
    dayLabel: (day: string, date: string, events: string) => `${day}, ${date}: ${events}`,
    events: (n: number) => (n === 1 ? '1 Termin' : `${n} Termine`),
    open: (n: string) => `, ${n} offen`,
    stars: starsDe,
    points: 'Punkte',
    waiting: 'Wartet auf OK',
    edit: 'Bearbeiten',
    waitingShort: ', wartet auf OK',
    streak: (n: string) => `${n} Tage am Stück`,
    approvedEarn: (name: string, stars: string) => `Freigegeben – ${name} bekommt ${stars}`,
    approvedRedeem: (stars: string) => `Eingelöst – ${stars} abgezogen`,
    returned: 'Zurückgegeben',
    jar: (goal: string) => `Sterne für ${goal}`,
    jarValue: (stars: string, goal: string) => `${stars} von ${goal} Sternen`,
    dayDone: (day: string, done: string, total: string) => `${day}: ${done} von ${total} erledigt`,
    dayEmpty: (day: string) => `${day}: keine Aufgaben`,
    pinWrong: 'Stimmt nicht. Bitte nochmal.',
    pinChecking: 'PIN wird geprüft …',
    pinDigits: (n: string, length: string) => `${n} von ${length} Ziffern`,
    pinDelete: 'Letzte Ziffer löschen',
    weatherIn: (place: string) => `Wetter in ${place}`,
    weatherDetails: 'Wetter-Details öffnen',
    weatherCourse: 'Verlauf',
    hour: (time: string) => `${time} Uhr`,
    rainPercent: (n: string) => `${n} Prozent Regenrisiko`,
    today: 'Heute',
    feelsLike: 'Gefühlt',
    rainRisk: 'Regenrisiko',
    rainRiskOf: (summary: string) => ` Regenrisiko, ${summary}`,
    next24Short: 'nächste 24 Std.',
    wind: 'Wind',
    uv: 'UV',
    sun: 'Sonne',
    next24: 'Nächste 24 Stunden',
    days7: '7 Tage',
    low: 'Tiefst ',
    high: 'Höchst ',
    uvLevel: { low: 'niedrig', moderate: 'mäßig', high: 'hoch', veryHigh: 'sehr hoch' },
    windLevel: { light: 'schwach', moderate: 'mäßig', fresh: 'frisch', stormy: 'stürmisch' },
    daylight: {
      dawn: 'Morgendämmerung',
      sunrise: 'Sonnenaufgang',
      morning: 'Morgen',
      noon: 'Mittag',
      evening: 'Abend',
      sunset: 'Sonnenuntergang',
      dusk: 'Abenddämmerung',
      duskDark: 'Später Abend',
      deep: 'Nacht',
      predawn: 'Vor der Dämmerung',
    },
  },
  en: {
    close: 'Close',
    password: 'Password',
    showPassword: 'Show password',
    again: 'Try again',
    mainNav: 'Main navigation',
    more: 'More',
    allAreas: 'Everything',
    everyone: 'Everyone',
    now: 'Now',
    happeningNow: 'Happening now',
    upNext: 'Up next',
    upNextLabel: (title: string, when: string) => `Up next: ${title}, ${when}`,
    upNextAt: (when: string) => `Up next · ${when}`,
    free: 'free',
    duration: (min: number) =>
      min >= 60 ? `${Math.floor(min / 60)} hr${min % 60 ? ` ${min % 60} min` : ''}` : `${min} min`,
    freeFor: (duration: string) => `${duration} free`,
    overlapWith: (title: string) => `at the same time as ${title}`,
    allDay: 'All day',
    dayLabel: (day: string, date: string, events: string) => `${day}, ${date}: ${events}`,
    events: (n: number) => (n === 1 ? '1 event' : `${n} events`),
    open: (n: string) => `, ${n} open`,
    stars: starsEn,
    points: 'points',
    waiting: 'Waiting for OK',
    edit: 'Edit',
    waitingShort: ', waiting for OK',
    streak: (n: string) => `${n} days in a row`,
    approvedEarn: (name: string, stars: string) => `Approved – ${name} gets ${stars}`,
    approvedRedeem: (stars: string) => `Redeemed – ${stars} taken off`,
    returned: 'Sent back',
    jar: (goal: string) => `Stars for ${goal}`,
    jarValue: (stars: string, goal: string) => `${stars} of ${goal} stars`,
    dayDone: (day: string, done: string, total: string) => `${day}: ${done} of ${total} done`,
    dayEmpty: (day: string) => `${day}: no tasks`,
    pinWrong: 'That’s not right. Please try again.',
    pinChecking: 'Checking the PIN …',
    pinDigits: (n: string, length: string) => `${n} of ${length} digits`,
    pinDelete: 'Delete last digit',
    weatherIn: (place: string) => `Weather in ${place}`,
    weatherDetails: 'Open weather details',
    weatherCourse: 'Forecast',
    hour: (time: string) => time,
    rainPercent: (n: string) => `${n} per cent chance of rain`,
    today: 'Today',
    feelsLike: 'Feels like',
    rainRisk: 'Chance of rain',
    rainRiskOf: (summary: string) => ` chance of rain, ${summary}`,
    next24Short: 'next 24 hrs',
    wind: 'Wind',
    uv: 'UV',
    sun: 'Sun',
    next24: 'Next 24 hours',
    days7: '7 days',
    low: 'Low ',
    high: 'High ',
    uvLevel: { low: 'low', moderate: 'moderate', high: 'high', veryHigh: 'very high' },
    windLevel: { light: 'light', moderate: 'moderate', fresh: 'fresh', stormy: 'stormy' },
    daylight: {
      dawn: 'Dawn',
      sunrise: 'Sunrise',
      morning: 'Morning',
      noon: 'Midday',
      evening: 'Evening',
      sunset: 'Sunset',
      dusk: 'Dusk',
      duskDark: 'Late evening',
      deep: 'Night',
      predawn: 'Before dawn',
    },
  },
} as const;

export const uiText = () => UI_TEXT[current];
